<template>
  <div>
    <!-- Floating bar: the agent's CallUp list -->
    <div v-if="count > 0" class="callup-bar">
      <div class="d-flex align-items-center gap-2 text-white">
        <i class="ri-clipboard-line text-primary fs-5"></i>
        <span class="fw-medium">Lista CallUp: {{ count }}</span>
      </div>
      <button v-if="canDial" class="btn btn-sm btn-success rounded-pill px-3" @click="open('start')">
        <i class="ri-phone-fill align-bottom me-1"></i> Iniciar marcación
      </button>
      <button class="btn btn-sm btn-outline-light rounded-pill px-3" @click="open('list')">Ver lista</button>
      <button class="btn btn-sm btn-primary rounded-pill px-3" @click="$emit('select-page')">Seleccionar página</button>
      <button class="btn btn-sm btn-dark rounded-pill border px-3" @click="clearAll">Deseleccionar</button>
      <button class="btn btn-sm btn-link text-white p-0 ms-1" title="Cerrar" @click="clearAll">
        <i class="ri-close-line fs-5"></i>
      </button>
    </div>

    <teleport to="body">
      <div v-if="modal" class="callup-overlay" @click.self="closeModal">
        <div class="callup-dialog" role="dialog" aria-modal="true">
          <div class="callup-dialog-header">
            <h5 class="mb-0">{{ modal === 'start' ? 'Iniciar marcación en CallUp' : 'Lista CallUp' }}</h5>
            <button type="button" class="btn-close" aria-label="Cerrar" @click="closeModal"></button>
          </div>

          <div class="callup-dialog-body">
            <div v-if="loading" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>Cargando…
            </div>
            <div v-else-if="loadError" class="alert alert-danger mb-0">{{ loadError }}</div>

            <template v-else>
              <div v-if="counts" class="d-flex flex-wrap gap-2 mb-3">
                <span class="badge bg-success-subtle text-success fs-12">{{ counts.ok }} listos para marcar</span>
                <span v-if="counts.dnc" class="badge bg-danger-subtle text-danger fs-12">{{ counts.dnc }} Do Not Call (excluidos)</span>
                <span v-if="counts.no_phone" class="badge bg-warning-subtle text-warning fs-12">{{ counts.no_phone }} sin teléfono válido</span>
                <span v-if="counts.forbidden" class="badge bg-secondary-subtle text-secondary fs-12">{{ counts.forbidden }} sin acceso</span>
              </div>

              <!-- Start options -->
              <template v-if="modal === 'start'">
                <label class="form-label fw-medium">¿Dónde quieres marcar?</label>
                <div class="d-flex flex-column gap-2 mb-3">
                  <label class="callup-option" :class="{ active: target === 'web' }">
                    <input v-model="target" class="form-check-input me-2" type="radio" value="web" />
                    <span><i class="ri-computer-line me-1"></i> Softphone de CallUp (navegador)</span>
                  </label>
                  <label class="callup-option" :class="{ active: target === 'mobile' }">
                    <input v-model="target" class="form-check-input me-2" type="radio" value="mobile" />
                    <span><i class="ri-smartphone-line me-1"></i> App móvil de CallUp</span>
                  </label>
                </div>

                <div class="form-check mb-1">
                  <input id="callupSkipRecent" v-model="skipRecent" class="form-check-input" type="checkbox" />
                  <label class="form-check-label" for="callupSkipRecent">
                    Omitir contactos a los que ya llamé en las últimas
                    <input v-model.number="skipHours" type="number" min="1" max="720" class="callup-hours" :disabled="!skipRecent" />
                    horas
                  </label>
                </div>
                <p class="text-muted fs-12 mt-2 mb-0">
                  Los contactos marcados como Do Not Call nunca se incluyen. Solo se marcan los que tú puedes ver en el CRM.
                </p>
              </template>

              <!-- List -->
              <div v-else class="callup-table-wrap">
                <table class="table table-sm align-middle mb-0">
                  <thead class="table-light">
                    <tr><th>Nombre</th><th>Teléfono</th><th>Tipo</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in rows" :key="row.entry.type + '-' + row.entry.id">
                      <td>{{ row.name || '#' + row.entry.id }}</td>
                      <td>{{ row.phone }}</td>
                      <td>{{ typeLabel[row.entry.type] }}</td>
                      <td><span class="badge" :class="'bg-' + statusInfo[row.status][1] + '-subtle text-' + statusInfo[row.status][1]">{{ statusInfo[row.status][0] }}</span></td>
                      <td class="text-end">
                        <button class="btn btn-sm btn-link text-danger p-0" title="Quitar de la lista" @click="removeRow(row)">
                          <i class="ri-close-circle-line fs-5"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>

          <div class="callup-dialog-footer">
            <button type="button" class="btn btn-light" @click="closeModal">{{ modal === 'start' ? 'Cancelar' : 'Cerrar' }}</button>
            <button
              v-if="modal === 'start'"
              type="button"
              class="btn btn-success"
              :disabled="loading || sending || !!loadError || readyCount === 0"
              @click="send"
            >
              <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="ri-phone-fill align-bottom me-1"></i>
              Iniciar marcación ({{ readyCount }})
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { callupList } from '@/services/callupList';

const STATUS_INFO = {
  ok: ['Listo', 'success'],
  dnc: ['Do Not Call', 'danger'],
  no_phone: ['Sin teléfono', 'warning'],
  forbidden: ['Sin acceso', 'secondary'],
};

export default {
  name: 'CallupDialerBar',
  emits: ['select-page', 'clear', 'remove'],
  data() {
    return {
      modal: null,            // 'start' | 'list' | null
      loading: false,
      loadError: '',
      rows: [],               // preview rows, each with the {type, id} entry it came from
      counts: null,
      sending: false,
      target: localStorage.getItem('crmCallupTarget') === 'mobile' ? 'mobile' : 'web',
      skipRecent: localStorage.getItem('crmCallupSkipRecent') === '1',
      skipHours: Number(localStorage.getItem('crmCallupSkipHours')) || 24,
      statusInfo: STATUS_INFO,
      typeLabel: { prospect: 'Prospecto', client: 'Cliente', agent: 'Agente' },
    };
  },
  computed: {
    count() {
      return callupList.state.entries.length;
    },
    // Only users with a CallUp account linked in their CRM profile can send a list.
    canDial() {
      return !!localStorage.getItem('callupUsername');
    },
    readyCount() {
      return this.counts ? this.counts.ok : 0;
    },
  },
  methods: {
    errorText(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.error) || fallback;
    },

    open(kind) {
      this.modal = kind;
      this.load();
    },

    closeModal() {
      this.modal = null;
    },

    clearAll() {
      callupList.clear();
      this.closeModal();
      this.$emit('clear');
    },

    async load() {
      this.loading = true;
      this.loadError = '';
      this.rows = [];
      this.counts = null;
      const entries = callupList.state.entries.map((e) => ({ type: e.type, id: e.id }));
      try {
        const { data } = await axios.post('/api/callup/preview-queue', {
          token: localStorage.getItem('jwt'),
          entries,
        });
        // The server answers in the same order, so each row keeps the entry it belongs to.
        this.rows = data.items.map((item, i) => ({ ...item, entry: entries[i] }));
        this.counts = data.counts;
      } catch (e) {
        this.loadError = this.errorText(e, 'No se pudo cargar la lista.');
      } finally {
        this.loading = false;
      }
    },

    removeRow(row) {
      callupList.remove(row.entry.type, row.entry.id);
      this.rows = this.rows.filter((r) => r !== row);
      const counts = { ok: 0, dnc: 0, no_phone: 0, forbidden: 0 };
      this.rows.forEach((r) => { counts[r.status] += 1; });
      this.counts = counts;
      this.$emit('remove', row.entry);
      if (callupList.count === 0) this.closeModal();
    },

    async send() {
      this.sending = true;
      const hours = this.skipRecent ? Math.min(Math.max(Math.round(this.skipHours) || 24, 1), 720) : 0;
      localStorage.setItem('crmCallupTarget', this.target);
      localStorage.setItem('crmCallupSkipRecent', this.skipRecent ? '1' : '0');
      localStorage.setItem('crmCallupSkipHours', String(hours || this.skipHours || 24));
      try {
        const { data } = await axios.post('/api/callup/send-queue', {
          token: localStorage.getItem('jwt'),
          entries: callupList.state.entries.map((e) => ({ type: e.type, id: e.id })),
          target: this.target,
          skipRecentHours: hours,
        });
        this.closeModal();
        await this.showResult(data, hours);
      } catch (e) {
        Swal.fire('No se pudo enviar la lista', this.errorText(e, 'Inténtalo de nuevo.'), 'error');
      } finally {
        this.sending = false;
      }
    },

    async showResult(data, hours) {
      const web = data.target === 'web';
      const notes = [];
      if (data.skippedRecent) notes.push(`${data.skippedRecent} omitidos (ya llamados en las últimas ${hours} h)`);
      if (data.counts.dnc) notes.push(`${data.counts.dnc} excluidos por Do Not Call`);
      if (data.counts.no_phone) notes.push(`${data.counts.no_phone} sin teléfono válido`);
      if (data.counts.forbidden) notes.push(`${data.counts.forbidden} sin acceso`);

      const lines = [];
      if (data.sent > 0) {
        lines.push(web
          ? 'La marcación empieza en tu softphone de CallUp. Si no lo tienes abierto, ábrelo ahora.'
          : 'Toca la notificación en tu teléfono para empezar a marcar.');
      } else {
        lines.push('No quedó ningún contacto por marcar.');
      }
      if (notes.length) lines.push(`<small class="text-muted">${notes.join(' · ')}</small>`);

      const result = await Swal.fire({
        icon: data.sent > 0 ? 'success' : 'info',
        title: data.sent > 0 ? `${data.sent} contacto(s) enviados a CallUp` : 'Nada que marcar',
        html: lines.join('<br>'),
        showCancelButton: web && data.sent > 0 && !!data.callupUrl,
        confirmButtonText: web && data.sent > 0 && data.callupUrl ? 'Abrir CallUp' : 'Listo',
        cancelButtonText: 'Cerrar',
      });
      if (web && result.isConfirmed && data.sent > 0 && data.callupUrl) {
        window.open(data.callupUrl, '_blank', 'noopener');
      }
    },
  },
};
</script>

<style scoped>
.callup-bar {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: #2b3136;
  border: 1px solid #40464c;
  border-radius: 50px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
  z-index: 1050;
  max-width: calc(100vw - 24px);
  flex-wrap: wrap;
  justify-content: center;
}

.callup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.callup-dialog {
  background: var(--vz-card-bg, #fff);
  color: var(--vz-body-color, #212529);
  border: 1px solid var(--vz-border-color, #dee2e6);
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.callup-dialog-header,
.callup-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 18px;
}

.callup-dialog-header { border-bottom: 1px solid var(--vz-border-color, #dee2e6); }
.callup-dialog-footer { border-top: 1px solid var(--vz-border-color, #dee2e6); justify-content: flex-end; }
.callup-dialog-body { padding: 18px; overflow-y: auto; }

.callup-table-wrap { max-height: 50vh; overflow-y: auto; }

.callup-option {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid var(--vz-border-color, #dee2e6);
  border-radius: 8px;
  cursor: pointer;
}
.callup-option.active { border-color: #0ab39c; background: rgba(10, 179, 156, 0.08); }

.callup-hours {
  width: 64px;
  margin: 0 4px;
  padding: 1px 6px;
  border: 1px solid var(--vz-border-color, #ced4da);
  border-radius: 4px;
  background: transparent;
  color: inherit;
}
</style>
